import { useEffect, useMemo, useState } from 'react'
import {
  collection, doc, onSnapshot, query, where, serverTimestamp,
} from 'firebase/firestore'
import { addDoc, setDoc, updateDoc, deleteDoc } from '../../core/db'
import { db } from '../../core/firebase'
import type {
  NtTerm, NtCourse, NtSegment, NtActivityKind, NtProposalType, NtRoster, NtDay, NtCostItem, NtSession, NtSelection, NtHost,
} from '../../core/types'

/**
 * NorseTerm's data layer and the two lists that stay in code.
 *
 * The palette and the project types are vocabulary, not district policy — see
 * the note on the NorseTerm types. Everything else reads from Firestore.
 */

/* ---------------- vocabulary (stays in code) ---------------- */

export const ACTIVITIES: Array<{ k: NtActivityKind; label: string; colour: string }> = [
  { k: 'instruct', label: 'Instruction / demo',      colour: '#c8102e' },
  { k: 'hands',    label: 'Hands-on work',           colour: '#1f6f4a' },
  { k: 'field',    label: 'Off-site / field',        colour: '#2b5d92' },
  { k: 'guest',    label: 'Guest / expert',          colour: '#7a4bb5' },
  { k: 'present',  label: 'Share out / demo day',    colour: '#b5761a' },
  { k: 'reflect',  label: 'Reflection / write-up',   colour: '#4a5568' },
  { k: 'setup',    label: 'Set-up / clean-up',       colour: '#8a1220' },
]
export const activity = (k: NtActivityKind) =>
  ACTIVITIES.find((a) => a.k === k) ?? ACTIVITIES[0]!

export const PROJECT_TYPES: Array<{ k: NtProposalType; label: string; blurb: string }> = [
  { k: 'shadow',  label: 'Job shadow',
    blurb: 'A week inside a workplace with a host who has agreed in writing.' },
  { k: 'college', label: 'College visits',
    blurb: 'Two or more scheduled campus visits with an itinerary and a comparison built afterward.' },
  { k: 'service', label: 'Volunteer project',
    blurb: 'Sustained service with one organisation that needs a specific thing done.' },
  { k: 'passion', label: 'Passion project',
    blurb: 'Their own research, build, performance or creation, with something to show Friday.' },
]

/**
 * The week is Tuesday to Friday. Tuesday–Thursday are course days, in each
 * course's own block; Friday 8:15–9:45 is artifact and open-house prep — every
 * student with the course they are making their artifact in — then the open
 * house (10:00–11:30) and March Madness. Index 0 is Tuesday. Times come from
 * the term's bell schedule (`partsFor`); Wednesday is an early out.
 */
export const DAY_LABELS = ['Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const
export const COURSE_DAYS = [0, 1, 2] as const
export const PREP_DAY = 3

/** The session a course's day happens in: its own block, except Friday, which is morning prep for everyone. */
export const planSession = (day: NtDay, courseSession: 0 | 1): 0 | 1 => (day === PREP_DAY ? 0 : courseSession)
export const SESSION_LABELS = ['Morning', 'Afternoon'] as const

/**
 * A block is two parts with a break between (the bell schedule): the unit a
 * teacher plans in. Friday morning prep is one part.
 */
export const PARTS = 2

/** The 2026–27 bell schedule, used until a term sets its own. */
const S = (start: string, end: string, label: string, block?: NtBlock): NtSession => ({ start, end, label, ...(block ? { block } : {}) })
export const DEFAULT_DAY_STRUCTURE: NtTerm['dayStructure'] = {
  full: [
    S('8:15', '9:45', 'Morning, part 1', 'AM'), S('10:00', '11:30', 'Morning, part 2', 'AM'),
    S('12:15', '1:45', 'Afternoon, part 1', 'PM'), S('2:00', '3:30', 'Afternoon, part 2', 'PM'),
  ],
  early: [
    S('8:15', '9:20', 'Morning, part 1', 'AM'), S('9:35', '10:40', 'Morning, part 2', 'AM'),
    S('11:20', '12:25', 'Afternoon, part 1', 'PM'), S('12:40', '1:45', 'Afternoon, part 2', 'PM'),
  ],
  prep: [S('8:15', '9:45', 'Artifact & open-house prep')],
  earlyOutDays: [1],
}

/**
 * The parts of one block on one day, with their times — Tuesday and Thursday
 * are full days, Wednesday is early out, Friday is morning prep only.
 */
export function partsFor(term: Pick<NtTerm, 'dayStructure'> | undefined, day: NtDay, block: NtBlock): NtSession[] {
  const ds = term?.dayStructure
  if (day === PREP_DAY) return ds?.prep?.length ? ds.prep : DEFAULT_DAY_STRUCTURE.prep!
  const early = (ds?.earlyOutDays ?? DEFAULT_DAY_STRUCTURE.earlyOutDays).includes(day)
  const list = (early ? ds?.early : ds?.full)?.filter((x) => x.block === block) ?? []
  return list.length >= PARTS ? list.slice(0, PARTS)
    : (early ? DEFAULT_DAY_STRUCTURE.early : DEFAULT_DAY_STRUCTURE.full).filter((x) => x.block === block)
}

/* ---------------- term ---------------- */

export const TERM_DEFAULT: NtTerm = {
  id: 'current', label: 'Norse Term', active: true, weekLabel: 'Not scheduled yet',
  deadlines: { courses: '', proposals: '', rankings: '' },
  rules: { ranksRequired: 6, ranksPerBlock: 3, defaultCap: 16, placementPriority: 'first-come' },
  dayStructure: DEFAULT_DAY_STRUCTURE,
  lunchWaves: { cutoffLastName: 'HEUBNER', a: '11:30–11:55', b: '11:55–12:20' },
}

/**
 * The open term. `enabled` because a listener the rules refuse does not just
 * fail quietly — it can wedge the client's local cache — so nothing subscribes
 * until somebody who may read it is signed in.
 */
export function useTerm(enabled = true): { term: NtTerm; loading: boolean } {
  const [term, setTerm] = useState<NtTerm>(TERM_DEFAULT)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!enabled) return
    return onSnapshot(
      query(collection(db, 'ntTerms'), where('active', '==', true)),
      (snap) => {
        const d = snap.docs[0]
        if (d) setTerm({ ...TERM_DEFAULT, ...(d.data() as NtTerm), id: d.id })
        setLoading(false)
      },
      () => setLoading(false),
    )
  }, [enabled])
  return { term, loading }
}

/**
 * Which days attendance is in for every one of a teacher's rosters — what
 * their checklist ticks off. One query across their courses, not one per day.
 */
/** Which of these courses have attendance in, as `${courseId}_${day}`. */
export function useMyAttendanceTaken(termId: string, courseIds: string[]): Set<string> {
  const [days, setDays] = useState<Set<string>>(new Set())
  const key = courseIds.slice().sort().join(',')
  useEffect(() => {
    if (!termId || !courseIds.length) { setDays(new Set()); return }
    return onSnapshot(
      query(collection(db, 'ntAttendance'), where('termId', '==', termId), where('courseId', 'in', courseIds.slice(0, 10))),
      (s) => {
        const taken = new Set<string>()
        s.docs.forEach((d) => {
          const a = d.data() as NtAttendance
          if (Object.keys(a.marks ?? {}).length) taken.add(`${a.courseId}_${a.day}`)
        })
        setDays(taken)
      },
      () => setDays(new Set()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId, key])
  return days
}

export const saveTerm = (termId: string, patch: Partial<NtTerm>) =>
  setDoc(doc(db, 'ntTerms', termId), { ...patch, updatedAt: serverTimestamp() }, { merge: true })

/* ---------------- courses ---------------- */

export function useCourses(termId: string): { courses: NtCourse[] | null; err: string | null } {
  const [courses, setCourses] = useState<NtCourse[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!termId) return
    // Equality filter only, sorted in the browser. An orderBy here would need a
    // composite index in Firestore — which means the screen is broken until
    // somebody deploys an index file, and the error it throws names a URL
    // rather than a cause. There are dozens of courses, not thousands.
    return onSnapshot(
      query(collection(db, 'ntCourses'), where('termId', '==', termId)),
      (s) => {
        setErr(null)
        setCourses(s.docs
          .map((d) => ({ id: d.id, ...d.data() }) as NtCourse)
          .sort((a, b) => a.title.localeCompare(b.title)))
      },
      (e) => setErr(e.message),
    )
  }, [termId])
  return { courses, err }
}

/** The number a reviewer signs off on. */
export const costTotal = (items: NtCostItem[]) =>
  items.reduce((n, i) => n + (i.amount || 0), 0)

export const BLANK_COURSE = (termId: string, uid: string, name: string, email = ''): Omit<NtCourse, 'id'> => ({
  termId, title: '', teacherUid: uid, teacherName: name, ...(email ? { teacherEmail: email.toLowerCase() } : {}), room: '',
  constraint: 'none', block: null, cap: 16, min: 6, costItems: [],
  status: 'draft', feedback: '', desc: '', needs: '', plan: [],
})

export const createCourse = async (c: Omit<NtCourse, 'id'>) =>
  (await addDoc(collection(db, 'ntCourses'), { ...c, updatedAt: serverTimestamp() })).id

/**
 * A teacher saving their own course. `block`, `status` and `feedback` are
 * stripped here as well as in the rules — a UI that sends a field the rules
 * reject fails the whole write, so the two have to agree.
 */
export function saveCourse(id: string, c: Partial<NtCourse>, submit?: boolean) {
  const { block, feedback, status, teacherUid, id: _drop, ...safe } = c as NtCourse
  void block; void feedback; void status; void teacherUid; void _drop
  return updateDoc(doc(db, 'ntCourses', id), {
    ...safe,
    ...(submit === undefined ? {} : { status: submit ? 'submitted' : 'draft' }),
    updatedAt: serverTimestamp(),
  })
}

export const deleteCourse = (id: string) => deleteDoc(doc(db, 'ntCourses', id))

/** The reviewer's move. Status and a note, nothing else. */
export const decideCourse = (id: string, approve: boolean, by: string, note: string) =>
  updateDoc(doc(db, 'ntCourses', id), {
    status: approve ? 'approved' : 'returned',
    feedback: note,
    reviewedBy: by,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })

/** The coordinator's move. Block only. */
export const assignBlock = (id: string, block: NtCourse['block']) =>
  updateDoc(doc(db, 'ntCourses', id), { block, updatedAt: serverTimestamp() })

/* ---------------- rosters ---------------- */

export function useMyRosters(termId: string, uid: string | undefined, email = '') {
  const [byUid, setByUid] = useState<NtRoster[]>([])
  const [byEmail, setByEmail] = useState<NtRoster[]>([])
  useEffect(() => {
    if (!termId || !uid) return
    return onSnapshot(
      query(collection(db, 'ntRosters'), where('termId', '==', termId), where('teacherUid', '==', uid)),
      (s) => setByUid(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtRoster)),
      () => setByUid([]),
    )
  }, [termId, uid])
  // Also by email: a credit recovery section is built onto a roster before
  // its teacher has signed in, so the roster may not carry their uid yet.
  useEffect(() => {
    if (!termId || !email) return
    return onSnapshot(
      query(collection(db, 'ntRosters'), where('termId', '==', termId), where('teacherEmail', '==', email)),
      (s) => setByEmail(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtRoster)),
      () => setByEmail([]),
    )
  }, [termId, email])
  return useMemo(() => {
    const m = new Map([...byUid, ...byEmail].map((r) => [r.id, r]))
    return [...m.values()]
  }, [byUid, byEmail])
}

/* ---------------- the day plan ---------------- */

export const seg = (
  day: NtDay, session: 0 | 1, slot: number, span: number, kind: NtActivityKind, text: string,
): NtSegment => ({ day, session, slot, span, kind, text })

/**
 * Plans are in parts: `slot` is the part (0 or 1) and `span` how many parts
 * (1, or 2 for the whole block). Plans written before the bell schedule was
 * real were in hours (slot 0–3); those are read as parts — the first two hours
 * as part 1, the last two as part 2 — so nothing anybody wrote is lost.
 */
export function normalizePlan(plan: NtSegment[]): NtSegment[] {
  if (!plan.some((p) => p.slot > 1 || p.span > 2)) return plan
  const out: NtSegment[] = []
  for (const p of plan) {
    const first = p.slot < 2 ? 0 : 1
    const last = p.slot + p.span - 1 < 2 ? 0 : 1
    const seg = { ...p, slot: first, span: last - first + 1 }
    // Two hour-blocks in the same part become the first of them.
    if (out.some((o) => o.day === seg.day && o.session === seg.session
      && o.slot <= seg.slot + seg.span - 1 && seg.slot <= o.slot + o.span - 1)) continue
    out.push(seg)
  }
  return out
}

/** Segments for one session, in part order. */
export const segmentsIn = (plan: NtSegment[], day: NtDay, session: 0 | 1) =>
  normalizePlan(plan).filter((p) => p.day === day && p.session === session).sort((a, b) => a.slot - b.slot)

/**
 * Parts with nothing planned across the three course days — six in all, two
 * a day. Friday morning prep is planned or not as the teacher likes, so it
 * does not count against them.
 */
/**
 * A plan shown in one block. Plans are Part 1 and Part 2 of each course day,
 * whichever block the course ends up in, so one written against morning times
 * follows the course into the afternoon rather than vanishing from view when
 * admin assigns the block. Friday prep is the same for everyone.
 */
export function planInBlock(plan: NtSegment[], session: 0 | 1): NtSegment[] {
  return normalizePlan(plan)
    .map((p) => (p.day === PREP_DAY ? p : { ...p, session: planSession(p.day as NtDay, session) }))
    .filter((p, i, all) => all.findIndex((q) => q.day === p.day && q.session === p.session && q.slot === p.slot) === i)
}

export function unplannedParts(plan: NtSegment[]): number {
  let planned = 0
  for (const d of COURSE_DAYS) {
    for (const s of [0, 1] as const) {
      planned += segmentsIn(plan, d, s).reduce((n, p) => n + p.span, 0)
    }
  }
  return Math.max(0, COURSE_DAYS.length * PARTS - planned)
}

/** The first free part of a session, or null when both are planned. */
export function nextFreeSlot(plan: NtSegment[], day: NtDay, session: 0 | 1, parts = PARTS): number | null {
  const taken = new Set<number>()
  segmentsIn(plan, day, session).forEach((p) => {
    for (let i = p.slot; i < p.slot + p.span; i++) taken.add(i)
  })
  for (let i = 0; i < parts; i++) if (!taken.has(i)) return i
  return null
}

/**
 * What a reviewer is really checking, computed rather than eyeballed.
 *
 * The prototype's own exemplars make the standard explicit: a strong course
 * changes across the week and ends in something shown. These are surfaced as
 * observations on the review screen, never as an automatic reject — a
 * four-day welding course that is hands-on every hour is fine, and a machine
 * should not be telling a teacher otherwise.
 */
export function planObservations(c: NtCourse): string[] {
  const out: string[] = []
  const gap = unplannedParts(c.plan)
  if (c.plan.length === 0) return ['No day plan yet.']
  if (gap >= 2) out.push(`${gap} of the six parts of the week are unplanned.`)
  const days = new Set(c.plan.map((p) => p.day).filter((d) => d !== PREP_DAY))
  if (days.size < COURSE_DAYS.length) out.push(`Only ${days.size} of the three course days have anything on them.`)
  const kinds = new Set(c.plan.map((p) => p.kind))
  if (kinds.size === 1) out.push('Every block is the same kind of activity.')
  if (!c.plan.some((p) => p.day === PREP_DAY)) {
    out.push('Nothing planned for Friday morning prep, which the open house follows.')
  }
  if (!c.needs.trim()) out.push('Nothing listed under what has to be arranged.')
  if (!(c.artifactOptions ?? []).some((o) => o.description.trim())) {
    out.push('No artifact options. Students need to know what they can make for the open house.')
  }
  return out
}

export const STATUS_LABEL: Record<NtCourse['status'], string> = {
  draft: 'Draft, not sent yet',
  submitted: 'Waiting on review',
  approved: 'Approved',
  returned: 'Sent back for changes',
}

/* ---------------- students, placement, attendance, projects ---------------- */

import {
  getDocs, deleteField,
} from 'firebase/firestore'
import { writeBatch } from '../../core/db'
import type {
  NtStudent, NtPlacement, NtProposal, NtAttendance, NtExemplar, NtAttendanceStatus,
} from '../../core/types'
import { runPlacement, balanceRosters, buildRosters, withHandPlacement, buildSchedules } from './placement'
import type { NtOverride, NtSchedule, NtBlock } from '../../core/types'

export function useStudents(termId: string, enabled: boolean) {
  const [students, setStudents] = useState<NtStudent[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!termId || !enabled) { setStudents([]); return }
    return onSnapshot(
      query(collection(db, 'ntStudents'), where('termId', '==', termId)),
      (s) => { setErr(null); setStudents(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtStudent)) },
      (e) => setErr(e.message),
    )
  }, [termId, enabled])
  return { students, err }
}

/* ---------------- course selections (students, in the app) ---------------- */

/**
 * Students rank morning and afternoon courses as two lists, this many each.
 * Older terms only have ranksRequired (a total), so half of it, rounded up.
 */
export const ranksPerBlock = (t: Pick<NtTerm, 'rules'>) =>
  t.rules.ranksPerBlock ?? Math.max(1, Math.ceil((t.rules.ranksRequired || 6) / 2))
/** The whole list: both blocks. */
export const ranksTotal = (t: Pick<NtTerm, 'rules'>) => 2 * ranksPerBlock(t)

export const selectionId = (termId: string, email: string) => `${termId}_${email.toLowerCase()}`

/** The approved catalog, for a student choosing. The query must say approved: the rules check it. */
export function useApprovedCourses(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtCourse[] | null>(null)
  useEffect(() => {
    if (!termId || !enabled) { setRows(null); return }
    return onSnapshot(
      query(collection(db, 'ntCourses'), where('termId', '==', termId), where('status', '==', 'approved')),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtCourse)
        .sort((a, b) => a.title.localeCompare(b.title))),
      () => setRows([]))
  }, [termId, enabled])
  return rows
}

/** A student's own selections; undefined = none yet, null = still loading. */
export function useMySelection(termId: string, email: string | undefined) {
  const [s, setS] = useState<NtSelection | null | undefined>(null)
  useEffect(() => {
    if (!termId || !email) return
    return onSnapshot(doc(db, 'ntSelections', selectionId(termId, email)),
      (d) => setS(d.exists() ? ({ id: d.id, ...d.data() } as NtSelection) : undefined),
      () => setS(undefined))
  }, [termId, email])
  return s
}

/** One seminar's selections, for their seminar teacher. The query must name them: the rules check every row. */
export function useSeminarSelections(termId: string, email: string | undefined) {
  const [rows, setRows] = useState<NtSelection[]>([])
  useEffect(() => {
    if (!termId || !email) { setRows([]); return }
    return onSnapshot(query(collection(db, 'ntSelections'), where('termId', '==', termId), where('seminarTeacherEmail', '==', email)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtSelection)),
      () => setRows([]))
  }, [termId, email])
  return rows
}

/** Every student's selections, for whoever runs placement or keeps track of students. */
export function useSelections(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtSelection[]>([])
  useEffect(() => {
    if (!termId || !enabled) { setRows([]); return }
    return onSnapshot(query(collection(db, 'ntSelections'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtSelection)),
      () => setRows([]))
  }, [termId, enabled])
  return rows
}

/**
 * Save a student's ranked list. The first time it is complete, ask the
 * server to stamp it: that is their place in line, and it never moves after.
 */
export async function saveSelection(termId: string, me: { email: string; studentId: string; name: string; seminarTeacherEmail: string },
  ranks: string[], required: number, existing: NtSelection | undefined) {
  const ref = doc(db, 'ntSelections', selectionId(termId, me.email))
  const stamp = ranks.length >= required && !existing?.submittedAt
  if (!existing) {
    await setDoc(ref, {
      termId, studentEmail: me.email.toLowerCase(), studentId: me.studentId, studentName: me.name,
      seminarTeacherEmail: me.seminarTeacherEmail, ranks,
      submittedAt: stamp ? serverTimestamp() : null, updatedAt: serverTimestamp(),
    })
  } else {
    await updateDoc(ref, { ranks, seminarTeacherEmail: me.seminarTeacherEmail, updatedAt: serverTimestamp(),
      ...(stamp ? { submittedAt: serverTimestamp() } : {}) })
  }
}

export function useProposals(termId: string, enabled: boolean) {
  const [proposals, setProposals] = useState<NtProposal[]>([])
  useEffect(() => {
    if (!termId || !enabled) return
    return onSnapshot(
      query(collection(db, 'ntProposals'), where('termId', '==', termId)),
      (s) => setProposals(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtProposal)),
      () => setProposals([]),
    )
  }, [termId, enabled])
  return proposals
}

export function usePlacement(termId: string, enabled: boolean) {
  const [placement, setPlacement] = useState<NtPlacement | null>(null)
  useEffect(() => {
    if (!termId || !enabled) return
    return onSnapshot(doc(db, 'ntPlacements', termId),
      (s) => setPlacement(s.exists() ? (s.data() as NtPlacement) : null),
      () => setPlacement(null))
  }, [termId, enabled])
  return placement
}

export function useExemplars(enabled = true) {
  const [rows, setRows] = useState<NtExemplar[]>([])
  useEffect(() => {
    if (!enabled) return
    return onSnapshot(collection(db, 'ntExemplars'),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtExemplar)),
      () => setRows([]))
  }, [enabled])
  return rows
}

export type ExemplarContent = Omit<NtExemplar, 'id'>

/** Create (no id) or replace one exemplar. Whole-doc write: the rules check its shape. */
export const saveExemplar = (id: string | undefined, e: ExemplarContent, by: string) => {
  const data = { type: e.type, good: e.good, title: e.title, why: e.why, detail: e.detail,
    updatedAt: serverTimestamp(), updatedBy: by }
  return id ? setDoc(doc(db, 'ntExemplars', id), data) : addDoc(collection(db, 'ntExemplars'), data)
}

export const deleteExemplar = (id: string) => deleteDoc(doc(db, 'ntExemplars', id))

/**
 * Parse the ranking export. Expected columns, header row required:
 *
 *   studentId, name, grade, rank1, rank2, … rankN
 *
 * plus, optionally, the Google Form's own `Timestamp` column — when the
 * student submitted, which decides first-come placement.
 *
 * Course columns hold the course *title*, because that is what a Google Form
 * writes and what a human can proof-read. Titles are matched to ids here, and
 * anything that does not match is reported rather than dropped — a silently
 * ignored ranking is a student who mysteriously got none of their choices.
 */
export function parseStudentCsv(text: string, courses: NtCourse[]) {
  const byTitle = new Map(courses.map((c) => [c.title.trim().toLowerCase(), c.id]))
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  if (lines.length < 2) return { rows: [], unmatched: [], badTimestamps: [], error: 'That file has no rows under the header.' }

  const header = splitCsvLine(lines[0]!).map((h) => h.trim().toLowerCase())
  const col = (want: string) => header.findIndex((h) => h === want)
  const iId = col('studentid'), iName = col('name'), iGrade = col('grade'), iTime = col('timestamp')
  if (iId < 0 || iName < 0 || iGrade < 0) {
    return { rows: [], unmatched: [], badTimestamps: [], error: 'Header must include studentId, name and grade.' }
  }
  const rankCols = header
    .map((h, i) => ({ h, i }))
    .filter((x) => /^rank\d+$/.test(x.h))
    .sort((a, b) => Number(a.h.slice(4)) - Number(b.h.slice(4)))
    .map((x) => x.i)

  const rows: Array<Omit<NtStudent, 'id' | 'termId'>> = []
  const unmatched = new Set<string>()
  const badTimestamps: string[] = []

  for (const line of lines.slice(1)) {
    const cells = splitCsvLine(line)
    const studentId = (cells[iId] ?? '').trim()
    const name = (cells[iName] ?? '').trim()
    if (!studentId || !name) continue
    const ranks: string[] = []
    for (const i of rankCols) {
      const title = (cells[i] ?? '').trim()
      if (!title) continue
      const id = byTitle.get(title.toLowerCase())
      if (id) ranks.push(id)
      else unmatched.add(title)
    }
    const row: Omit<NtStudent, 'id' | 'termId'> = { studentId, name, grade: Number((cells[iGrade] ?? '').trim()) || 9, ranks }
    if (iTime >= 0) {
      const at = parseFormTimestamp((cells[iTime] ?? '').trim())
      if (at) row.submittedAt = at
      else badTimestamps.push(name)
    }
    rows.push(row)
  }
  return { rows, unmatched: [...unmatched], badTimestamps, error: null as string | null }
}

/**
 * A Google Form timestamp — `1/16/2026 14:03:22`, month first, local time —
 * as ISO. Anything else is tried as an ordinary date; null if it is not one,
 * so a bad cell is reported rather than quietly placed last.
 */
function parseFormTimestamp(v: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(v)
  const d = m
    ? new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0))
    : new Date(v)
  return v && !isNaN(d.getTime()) ? d.toISOString() : null
}

/** Minimal CSV: handles quoted fields with commas, which names have. */
function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++ }
      else if (ch === '"') quoted = false
      else cur += ch
    } else if (ch === '"') quoted = true
    else if (ch === ',') { out.push(cur); cur = '' }
    else cur += ch
  }
  out.push(cur)
  return out
}

/** Replace the roster wholesale. An import is the whole file, not a merge. */
export async function importStudents(termId: string, rows: Array<Omit<NtStudent, 'id' | 'termId'>>) {
  const existing = await getDocs(query(collection(db, 'ntStudents'), where('termId', '==', termId)))
  // Firestore caps a batch at 500 writes, and a 4A high school clears that.
  const chunks: Array<Array<() => void>> = []
  let batch = writeBatch(db)
  let n = 0
  const flush: Array<Promise<void>> = []
  const commit = () => { flush.push(batch.commit()); batch = writeBatch(db); n = 0 }

  existing.docs.forEach((d) => {
    batch.delete(d.ref); if (++n >= 450) commit()
  })
  rows.forEach((r) => {
    batch.set(doc(db, 'ntStudents', `${termId}_${r.studentId}`), { ...r, termId })
    if (++n >= 450) commit()
  })
  commit()
  await Promise.all(flush)
  void chunks
  return rows.length
}

/**
 * Run placement and write the result — plus one roster document per course, in
 * the same action. A roster one run out of date is worse than no roster,
 * because the teacher believes it.
 */
export async function runAndSavePlacement(
  termId: string,
  courses: NtCourse[],
  students: NtStudent[],
  proposals: NtProposal[],
  priority: NtTerm['rules']['placementPriority'],
  by: string,
  balance: boolean,
  overrides: NtOverride[] = [],
) {
  let placement = runPlacement({ termId, courses, students, proposals, priority, overrides })
  let moved = 0
  if (balance) {
    const res = balanceRosters(placement, courses, students)
    placement = res.placement
    moved = res.moved
  }
  const rosters = buildRosters(placement, courses)

  const batch = writeBatch(db)
  batch.set(doc(db, 'ntPlacements', termId), {
    ...placement, runAt: serverTimestamp(), runBy: by,
  })
  rosters.forEach((r) => {
    const { id, ...rest } = r
    batch.set(doc(db, 'ntRosters', id), { ...rest, builtAt: serverTimestamp() })
  })
  await batch.commit()
  await writeSchedules(termId, placement, courses)
  return { placement, moved, rosters: rosters.length }
}

/**
 * Every student's schedule, from a placement — keeping artifact choices from
 * the last one. Emails come from the roster Sheet, which is how a signed-in
 * student finds their own. Chunked: Firestore takes 500 writes a batch, and a
 * whole high school is more than that.
 */
async function writeSchedules(termId: string, placement: NtPlacement, courses: NtCourse[], only?: string) {
  const [roster, prev] = await Promise.all([
    getDocs(collection(db, 'students')),
    getDocs(query(collection(db, 'ntSchedules'), where('termId', '==', termId))),
  ])
  const emailOf = new Map(roster.docs.map((d) => [String(d.get('studentId')), d.id]))
  const previous = new Map(prev.docs.map((d) => [String(d.get('studentId')), d.data() as NtSchedule]))
  const schedules = buildSchedules(placement, courses, emailOf, previous)
    .filter((sch) => !only || sch.studentId === only)
  for (let i = 0; i < schedules.length; i += 450) {
    const b = writeBatch(db)
    schedules.slice(i, i + 450).forEach(({ id, ...rest }) => b.set(doc(db, 'ntSchedules', id), rest))
    await b.commit()
  }
}

/** A student's own schedule, found by their email. `undefined` = placement has not reached them. */
export function useMySchedule(termId: string, email: string | undefined) {
  const [row, setRow] = useState<NtSchedule | null | undefined>(null)
  useEffect(() => {
    if (!termId || !email) return
    return onSnapshot(query(collection(db, 'ntSchedules'), where('termId', '==', termId), where('studentEmail', '==', email)),
      (s) => setRow(s.docs[0] ? ({ id: s.docs[0].id, ...s.docs[0].data() } as NtSchedule) : undefined),
      () => setRow(undefined))
  }, [termId, email])
  return row
}

/** Every schedule — the office's artifact view and Friday's attendance. */
export function useTermSchedules(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtSchedule[]>([])
  useEffect(() => {
    if (!termId || !enabled) { setRows([]); return }
    return onSnapshot(query(collection(db, 'ntSchedules'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtSchedule)),
      () => setRows([]))
  }, [termId, enabled])
  return rows
}

/**
 * The schedules of a teacher's own students — morning and afternoon asked
 * separately, because the rules check each row against the teacher.
 */
export function useMyStudentsSchedules(termId: string, uid: string | undefined, enabled: boolean) {
  const [am, setAm] = useState<NtSchedule[]>([])
  const [pm, setPm] = useState<NtSchedule[]>([])
  useEffect(() => {
    if (!termId || !uid || !enabled) { setAm([]); setPm([]); return }
    const sub = (field: 'amTeacherUid' | 'pmTeacherUid', set: (r: NtSchedule[]) => void) => onSnapshot(
      query(collection(db, 'ntSchedules'), where('termId', '==', termId), where(field, '==', uid)),
      (s) => set(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtSchedule)), () => set([]))
    const a = sub('amTeacherUid', setAm)
    const b = sub('pmTeacherUid', setPm)
    return () => { a(); b() }
  }, [termId, uid, enabled])
  return useMemo(() => [...am, ...pm.filter((p) => !am.some((a) => a.id === p.id))], [am, pm])
}

/** Choose (student) or set (office) which course a student makes their artifact in. */
export const setArtifactBlock = (scheduleId: string, block: NtBlock, by: string) =>
  updateDoc(doc(db, 'ntSchedules', scheduleId), { artifactBlock: block, artifactSetBy: by })

export function useAttendance(termId: string, courseId: string) {
  const [rows, setRows] = useState<Record<number, NtAttendance>>({})
  useEffect(() => {
    if (!termId || !courseId) return
    return onSnapshot(
      query(collection(db, 'ntAttendance'), where('termId', '==', termId), where('courseId', '==', courseId)),
      (s) => {
        const map: Record<number, NtAttendance> = {}
        s.docs.forEach((d) => { const a = { id: d.id, ...d.data() } as NtAttendance; map[a.day] = a })
        setRows(map)
      },
      () => setRows({}),
    )
  }, [termId, courseId])
  return rows
}

/**
 * Save a course's attendance for one day: every rostered student's mark (so
 * the document existing means "taken"), any notes for the office, and who took
 * it by name — the office needs a person to ask, not a uid.
 */
export const markAttendance = (
  termId: string, courseId: string, day: NtDay,
  marks: Record<string, NtAttendanceStatus>, notes: Record<string, string>, by: string,
) => setDoc(doc(db, 'ntAttendance', `${termId}_${courseId}_${day}`), {
  termId, courseId, day, marks,
  notes: Object.fromEntries(Object.entries(notes).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v)),
  takenBy: by, takenAt: serverTimestamp(),
})

/** Every course's attendance for the term — the office's reports. */
export function useTermAttendance(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtAttendance[]>([])
  useEffect(() => {
    if (!termId || !enabled) { setRows([]); return }
    return onSnapshot(query(collection(db, 'ntAttendance'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtAttendance)),
      () => setRows([]))
  }, [termId, enabled])
  return rows
}

/** Every course's roster for the term — who should be marked. */
export function useAllRosters(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtRoster[]>([])
  useEffect(() => {
    if (!termId || !enabled) { setRows([]); return }
    return onSnapshot(query(collection(db, 'ntRosters'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtRoster)),
      () => setRows([]))
  }, [termId, enabled])
  return rows
}

/* ---------------- independent-project proposals ---------------- */

import type { NtProposalEvent, NtProposalStatus, StudentDoc } from '../../core/types'
import {
  orderBy,
} from 'firebase/firestore'

/** One per student per term, so the id is knowable without a query. */
export const proposalId = (termId: string, studentEmail: string) => `${termId}_${studentEmail.toLowerCase()}`

/** A student's own proposal for this term, live. `undefined` = not started. */
export function useMyProposal(termId: string, email: string | undefined) {
  const [p, setP] = useState<NtProposal | null | undefined>(null)
  useEffect(() => {
    if (!termId || !email) return
    return onSnapshot(doc(db, 'ntProposals', proposalId(termId, email)),
      (s) => setP(s.exists() ? ({ id: s.id, ...s.data() } as NtProposal) : undefined),
      () => setP(undefined))
  }, [termId, email])
  return p
}

/** Proposals routed to one seminar teacher. The query must name them — the rules check every row. */
export function useSeminarProposals(termId: string, email: string | undefined) {
  const [rows, setRows] = useState<NtProposal[]>([])
  useEffect(() => {
    if (!termId || !email) return
    return onSnapshot(
      query(collection(db, 'ntProposals'), where('termId', '==', termId), where('seminarTeacherEmail', '==', email)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtProposal)),
      () => setRows([]))
  }, [termId, email])
  return rows
}

/** The students a seminar teacher has, from the roster. */
export function useSeminarStudents(email: string | undefined) {
  const [rows, setRows] = useState<StudentDoc[]>([])
  useEffect(() => {
    if (!email) return
    return onSnapshot(
      query(collection(db, 'students'), where('seminarTeacherEmail', '==', email)),
      (s) => setRows(s.docs.map((d) => d.data() as StudentDoc).filter((r) => r.active)),
      () => setRows([]))
  }, [email])
  return rows
}

export function useProposalEvents(id: string | undefined) {
  const [rows, setRows] = useState<NtProposalEvent[]>([])
  useEffect(() => {
    if (!id) return
    return onSnapshot(query(collection(db, 'ntProposals', id, 'events'), orderBy('at')),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtProposalEvent)),
      () => setRows([]))
  }, [id])
  return rows
}

export type ProposalContent = Pick<NtProposal, 'type' | 'types' | 'title' | 'detail' | 'host' | 'hosts' | 'plan' | 'artifact' | 'proofs' | 'partnersText'>

export const BLANK_HOST = (): NtHost => ({ org: '', name: '', role: '', phone: '', email: '', address: '', days: [], proofs: [] })

/** What a host card still needs before the proposal can be submitted; empty means complete. */
export function hostGaps(h: NtHost, proofRequired: boolean): string[] {
  return [
    !h.org.trim() && 'the place',
    !h.name.trim() && 'a contact name',
    !h.phone.trim() && !h.email.trim() && 'a phone or email',
    !h.address.trim() && 'an address',
    !h.days.length && 'which days',
    proofRequired && !h.proofs.length && 'proof they agreed',
  ].filter(Boolean) as string[]
}

/** The hosts as one line each, for the plain-text host field older screens read. */
export const hostSummary = (hosts: NtHost[]) => hosts.map((h) =>
  [h.org, [h.name, h.role].filter(Boolean).join(', '), h.phone, h.email, h.address].filter((x) => x.trim()).join(' · ')).join('\n')

/** Job shadows and college visits need proof the host agreed before they can be submitted. */
export const needsProof = (types: NtProposalType[] | undefined) => (types ?? []).some((t) => t === 'shadow' || t === 'college')

/** Every kind a proposal includes: its list, or for older proposals just its one type. */
export const proposalTypes = (p: Pick<NtProposal, 'type' | 'types'>): NtProposalType[] =>
  p.types?.length ? p.types : [p.type]

/**
 * The blocks of an independent project's week, on the real bell schedule:
 * Tuesday to Thursday, morning and afternoon, two parts each (Wednesday's
 * early-out times), then Friday morning's artifact and open-house prep. A
 * project student is out all day, so every block needs a plan and a place.
 */
export function projectSlots(term: Pick<NtTerm, 'dayStructure'> | undefined) {
  const out: Array<{ day: NtDay; block: NtBlock; part: 0 | 1; start: string; end: string; label: string }> = []
  for (const d of COURSE_DAYS as readonly NtDay[]) {
    for (const block of ['AM', 'PM'] as const) {
      partsFor(term, d, block).forEach((p, i) => out.push({ day: d, block, part: i as 0 | 1, start: p.start, end: p.end,
        label: `${block === 'AM' ? 'Morning' : 'Afternoon'}, part ${i + 1}` }))
    }
  }
  const prep = partsFor(term, PREP_DAY, 'AM')[0]
  if (prep) out.push({ day: PREP_DAY, block: 'AM', part: 0, start: prep.start, end: prep.end, label: 'Artifact & open-house prep' })
  return out
}

/** Friday is on campus for everyone, project students included: never off site. */
export const FRIDAY_WHERE = 'At school'

/** Blocks still missing what or where — none means the plan is complete. Friday needs only what. */
export function projectPlanGaps(plan: NtProposal['plan'], term: Pick<NtTerm, 'dayStructure'> | undefined): number {
  return projectSlots(term).filter((sl) => {
    const b = (plan ?? []).find((x) => x.day === sl.day && x.block === sl.block && x.part === sl.part)
    return !b?.what.trim() || (sl.day !== PREP_DAY && !b?.where.trim())
  }).length
}

/**
 * The student saving their proposal — creating it the first time, editing it
 * after. Routing (seminar teacher, student ID) always comes from their roster
 * entry, never from the form; the rules check it matches.
 */
export async function saveProposal(termId: string, me: StudentDoc, content: ProposalContent, existing: NtProposal | undefined) {
  const ref = doc(db, 'ntProposals', proposalId(termId, me.email))
  const routing = { seminarTeacherEmail: me.seminarTeacherEmail, seminarTeacherName: me.seminarTeacherName ?? '' }
  if (!existing) {
    await setDoc(ref, {
      termId, studentEmail: me.email, studentId: me.studentId, studentName: me.displayName,
      ...routing, ...content, status: 'draft', feedback: '',
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    })
  } else {
    await updateDoc(ref, { ...content, ...routing, updatedAt: serverTimestamp() })
  }
}

export const deleteDraft = (termId: string, email: string) =>
  deleteDoc(doc(db, 'ntProposals', proposalId(termId, email)))

/**
 * Every status change, from anybody, goes through here: the change and its
 * history entry in one batch. The rules refuse a status change without one, so
 * the history can never have a gap.
 */
export async function moveProposal(
  id: string,
  change: { status: NtProposalStatus; feedback?: string; checkedByName?: string; decidedByName?: string }
    & Partial<ProposalContent> & { seminarTeacherEmail?: string; seminarTeacherName?: string },
  event: { byEmail: string; byName: string; action: NtProposalEvent['action']; note?: string },
) {
  const ref = doc(db, 'ntProposals', id)
  const ev = doc(collection(ref, 'events'))
  const batch = writeBatch(db)
  batch.update(ref, { ...change, lastEventId: ev.id, updatedAt: serverTimestamp() })
  batch.set(ev, {
    at: serverTimestamp(), byEmail: event.byEmail, byName: event.byName,
    action: event.action, statusAfter: change.status, note: event.note ?? '',
  })
  await batch.commit()
}

/** Where a proposal sits, from the point of view of whoever is reading it. */
export function proposalWhere(p: NtProposal, viewer: 'student' | 'seminar' | 'decider'): string {
  const teacher = p.seminarTeacherName || 'your seminar teacher'
  switch (p.status) {
    case 'draft': return viewer === 'student' ? 'Draft, only you can see it' : 'Draft'
    case 'submitted': return viewer === 'student' ? `Waiting on ${teacher}`
      : viewer === 'seminar' ? 'Waiting on you' : `With ${p.seminarTeacherName || 'the seminar teacher'}`
    case 'checked': return viewer === 'decider' ? 'Ready for approval'
      : `Checked off${p.checkedByName ? ` by ${p.checkedByName}` : ''}, waiting on final approval`
    case 'approved': return 'Approved'
    case 'returned': return viewer === 'student' ? 'Sent back, see the note' : 'Sent back to the student'
  }
}

export const clearField = deleteField

/* ---------------- visits, awards, history ---------------- */

import type { NtVisit, NtAward } from '../../core/types'

export function useVisits(termId: string) {
  const [rows, setRows] = useState<NtVisit[]>([])
  useEffect(() => {
    if (!termId) return
    return onSnapshot(query(collection(db, 'ntVisits'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtVisit)),
      () => setRows([]))
  }, [termId])
  return rows
}

export const assignVisit = (v: Omit<NtVisit, 'id'>) =>
  addDoc(collection(db, 'ntVisits'), v)

export const unassignVisit = (id: string) => deleteDoc(doc(db, 'ntVisits', id))

/** The visitor's own note. The rules allow exactly these three fields. */
export const logVisit = (id: string, note: string, rating: number) =>
  updateDoc(doc(db, 'ntVisits', id), { note, rating, loggedAt: serverTimestamp() })

/**
 * Spread visits so every course gets seen once before anyone gets seen twice,
 * and no visitor is in two rooms at the same time.
 *
 * Deliberately a suggestion the coordinator can edit rather than a schedule
 * that gets written straight to the database — the person doing this knows
 * that the principal has a board meeting Tuesday afternoon and the algorithm
 * does not.
 */
export function suggestVisits(
  courses: NtCourse[],
  visitors: Array<{ email: string; name: string }>,
  existing: NtVisit[],
): Array<Omit<NtVisit, 'id'>> {
  const live = courses.filter((c) => c.status === 'approved' && c.block)
  const seen = new Set(existing.map((v) => v.courseId))
  const busy = new Set(existing.map((v) => `${v.visitorEmail}_${v.day}_${v.session}`))
  const out: Array<Omit<NtVisit, 'id'>> = []
  if (!visitors.length) return out

  let i = 0
  for (const c of live) {
    if (seen.has(c.id)) continue
    // A course only exists during its own block, so the session is forced.
    const session: 0 | 1 = c.block === 'AM' ? 0 : 1
    let placed = false
    // Course days only — Friday morning is artifact prep, not the course.
    for (const day of COURSE_DAYS as readonly NtDay[]) {
      for (let n = 0; n < visitors.length && !placed; n++) {
        const v = visitors[(i + n) % visitors.length]!
        const key = `${v.email}_${day}_${session}`
        if (busy.has(key)) continue
        busy.add(key)
        out.push({
          termId: c.termId, courseId: c.id, day, session,
          visitorEmail: v.email, visitorName: v.name,
        })
        placed = true
        i++
      }
      if (placed) break
    }
  }
  return out
}

export function useAwards(termId: string) {
  const [rows, setRows] = useState<NtAward[]>([])
  useEffect(() => {
    if (!termId) return
    return onSnapshot(query(collection(db, 'ntAwards'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtAward)),
      () => setRows([]))
  }, [termId])
  return rows
}

export const saveAward = (a: Omit<NtAward, 'id'>, by: string) =>
  addDoc(collection(db, 'ntAwards'), { ...a, recordedBy: by, recordedAt: serverTimestamp() })

export const removeAward = (id: string) => deleteDoc(doc(db, 'ntAwards', id))

/** Every term, newest first. The history tab reads from this. */
export function useAllTerms() {
  const [rows, setRows] = useState<NtTerm[]>([])
  useEffect(() => onSnapshot(collection(db, 'ntTerms'),
    (s) => setRows(s.docs
      .map((d) => ({ ...TERM_DEFAULT, ...(d.data() as NtTerm), id: d.id }))
      .sort((a, b) => b.label.localeCompare(a.label))),
    () => setRows([])), [])
  return rows
}

/* ---------------- hand placements ---------------- */

export function useOverrides(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtOverride[]>([])
  useEffect(() => {
    if (!termId || !enabled) { setRows([]); return }
    return onSnapshot(query(collection(db, 'ntOverrides'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtOverride)),
      () => setRows([]))
  }, [termId, enabled])
  return rows
}

/**
 * Place a student by hand: the override (so every future run keeps them
 * there), the placement with just them moved, and the rebuilt rosters — one
 * batch, so a teacher's roster never disagrees with the placement.
 */
export async function saveHandPlacement(
  termId: string, o: { studentId: string; amCourseId?: string; pmCourseId?: string; note?: string },
  placement: NtPlacement, courses: NtCourse[], students: NtStudent[], by: string,
) {
  const id = `${termId}_${o.studentId}`
  const override: NtOverride = { id, termId, ...o, by }
  const next = withHandPlacement(placement, courses, students, override)
  const batch = writeBatch(db)
  const { id: _id, ...data } = override
  void _id
  batch.set(doc(db, 'ntOverrides', id), {
    ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined)), at: serverTimestamp(),
  })
  batch.set(doc(db, 'ntPlacements', termId), { ...next, runAt: placement.runAt ?? serverTimestamp(), runBy: placement.runBy ?? by })
  buildRosters(next, courses).forEach((r) => {
    const { id: rid, ...rest } = r
    batch.set(doc(db, 'ntRosters', rid), { ...rest, builtAt: serverTimestamp() })
  })
  await batch.commit()
  await writeSchedules(termId, next, courses, o.studentId)
}

/**
 * Let placement decide again. The student stays where they are until the next
 * run — removing a lock is not the same as moving them — but that run is free
 * to place them normally.
 */
export async function removeHandPlacement(termId: string, studentId: string, placement: NtPlacement | null) {
  const batch = writeBatch(db)
  batch.delete(doc(db, 'ntOverrides', `${termId}_${studentId}`))
  if (placement) {
    const rows = placement.rows.map((r) => {
      if (r.studentId !== studentId) return r
      const { amLocked: _a, pmLocked: _p, ...rest } = r
      void _a; void _p
      return rest
    })
    batch.update(doc(db, 'ntPlacements', termId), { rows })
  }
  await batch.commit()
}

/* ---------------- credit recovery ---------------- */

export interface SectionInput { title: string; block: NtBlock; teacherEmail: string; teacherName: string; room: string; cap: number; desc: string }

/**
 * A credit recovery section: admin-made, approved from the start, never in
 * the catalog. Named by the teacher's email; their uid arrives when they
 * claim it on first sign-in (`claimSections`).
 */
export async function createSection(termId: string, x: SectionInput, pairId?: string) {
  await addDoc(collection(db, 'ntCourses'), {
    ...(pairId ? { pairId } : {}),
    termId, title: x.title.trim(), teacherUid: '', teacherName: x.teacherName, teacherEmail: x.teacherEmail.toLowerCase(),
    room: x.room.trim(), constraint: x.block, block: x.block, cap: x.cap, min: 0, costItems: [], artifactOptions: [],
    status: 'approved', feedback: '', desc: x.desc.trim(), needs: '', plan: [], assignedOnly: true,
    updatedAt: serverTimestamp(),
  })
}

/** An all-day section: a morning and an afternoon half, linked, with the same teacher, room and seats. */
export async function createAllDaySection(termId: string, x: Omit<SectionInput, 'block'>) {
  const pairId = doc(collection(db, 'ntCourses')).id
  await createSection(termId, { ...x, block: 'AM' }, pairId)
  await createSection(termId, { ...x, block: 'PM' }, pairId)
}

/** Change a section. A new teacher means a new claim, so the uid is cleared. */
export async function updateSection(c: NtCourse, x: Omit<SectionInput, 'block'>) {
  const email = x.teacherEmail.toLowerCase()
  await updateDoc(doc(db, 'ntCourses', c.id), {
    title: x.title.trim(), teacherEmail: email, teacherName: x.teacherName, room: x.room.trim(), cap: x.cap, desc: x.desc.trim(),
    ...(email !== c.teacherEmail ? { teacherUid: '' } : {}), updatedAt: serverTimestamp(),
  })
}

/** A teacher taking up the sections admin named them for. */
export async function claimSections(courses: NtCourse[], uid: string) {
  await Promise.all(courses.map((c) => updateDoc(doc(db, 'ntCourses', c.id), { teacherUid: uid })))
}

/**
 * Put a student in (or take them out of) a block by hand, keeping whatever
 * the other block already holds: a hand placement is one record per student,
 * and credit recovery in the morning must not wipe an afternoon placement.
 * `null` clears that block. With no placement run yet, only the record is
 * written; the run will honour it.
 */
export async function placeInBlock(
  termId: string, studentId: string, block: NtBlock, courseId: string | null, note: string | undefined,
  existing: NtOverride | undefined, placement: NtPlacement | null, courses: NtCourse[], students: NtStudent[], by: string,
) {
  return placeInBlocks(termId, studentId, { [block]: courseId }, note, existing, placement, courses, students, by)
}

/** Both blocks at once (all-day credit recovery), as one hand placement. A block left out keeps what it has. */
export async function placeInBlocks(
  termId: string, studentId: string, patch: Partial<Record<NtBlock, string | null>>, note: string | undefined,
  existing: NtOverride | undefined, placement: NtPlacement | null, courses: NtCourse[], students: NtStudent[], by: string,
) {
  const am = 'AM' in patch ? patch.AM ?? undefined : existing?.amCourseId
  const pm = 'PM' in patch ? patch.PM ?? undefined : existing?.pmCourseId
  const o = { studentId, ...(am ? { amCourseId: am } : {}), ...(pm ? { pmCourseId: pm } : {}),
    ...(note ?? existing?.note ? { note: note ?? existing!.note } : {}) }
  if (!am && !pm) return removeHandPlacement(termId, studentId, placement)
  if (placement) return saveHandPlacement(termId, o, placement, courses, students, by)
  await setDoc(doc(db, 'ntOverrides', `${termId}_${studentId}`), { termId, ...o, by, at: serverTimestamp() })
}

/* ---------------- prep dashboard ---------------- */

/**
 * Everyone holding the Teacher role. The rules let NorseTerm admins and office
 * list these and only these, so the query has to ask for exactly that.
 */
export function useNorseTermTeachers(enabled: boolean) {
  const [rows, setRows] = useState<Array<{ email: string; name: string }>>([])
  useEffect(() => {
    if (!enabled) { setRows([]); return }
    return onSnapshot(
      query(collection(db, 'users'), where('caps', 'array-contains-any', ['norseterm.teach@hs', 'norseterm.teach@district'])),
      (s) => setRows(s.docs
        .filter((d) => d.get('active') !== false)
        .map((d) => ({ email: d.id, name: String(d.get('displayName') ?? d.id) }))),
      () => setRows([]))
  }, [enabled])
  return rows
}

/* ---------------- course exemplars ---------------- */

import type { NtCourseExemplar } from '../../core/types'

export function useCourseExemplars(enabled = true) {
  const [rows, setRows] = useState<NtCourseExemplar[]>([])
  useEffect(() => {
    if (!enabled) { setRows([]); return }
    return onSnapshot(collection(db, 'ntCourseExemplars'),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtCourseExemplar)
        .sort((a, b) => Number(b.good) - Number(a.good) || a.title.localeCompare(b.title))),
      () => setRows([]))
  }, [enabled])
  return rows
}

export type CourseExemplarContent = Omit<NtCourseExemplar, 'id'>

export const saveCourseExemplar = (id: string | undefined, e: CourseExemplarContent, by: string) => {
  const data = {
    title: e.title, good: e.good, why: e.why, desc: e.desc, needs: e.needs, room: e.room, block: e.block,
    cap: Math.round(e.cap), min: Math.round(e.min), costItems: e.costItems,
    artifactOptions: (e.artifactOptions ?? []).filter((o) => o.description.trim()), plan: e.plan,
    updatedAt: serverTimestamp(), updatedBy: by,
  }
  return id ? setDoc(doc(db, 'ntCourseExemplars', id), data) : addDoc(collection(db, 'ntCourseExemplars'), data)
}

export const deleteCourseExemplar = (id: string) => deleteDoc(doc(db, 'ntCourseExemplars', id))

/* ---------------- open house ---------------- */

import type { NtPresented } from '../../core/types'

export function usePresented(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtPresented[] | null>(null)
  useEffect(() => {
    if (!termId || !enabled) { setRows(null); return }
    return onSnapshot(query(collection(db, 'ntPresented'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtPresented)),
      () => setRows([]))
  }, [termId, enabled])
  return rows
}

/** Mark whether a student presented; null clears it back to unchecked. */
export async function markPresented(termId: string, studentId: string, presented: boolean | null, note: string, by: string) {
  const ref = doc(db, 'ntPresented', `${termId}_${studentId}`)
  if (presented === null) return deleteDoc(ref)
  await setDoc(ref, { termId, studentId, presented, ...(note.trim() ? { note: note.trim().slice(0, 300) } : {}), by, at: serverTimestamp() })
}

/* ---------------- project check-ins ---------------- */

import type { NtCheckin } from '../../core/types'

/** Approved projects, for staff checking in on off-site students. The query has to ask for approved ones. */
export function useApprovedProposals(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtProposal[] | null>(null)
  useEffect(() => {
    if (!termId || !enabled) { setRows(null); return }
    return onSnapshot(query(collection(db, 'ntProposals'), where('termId', '==', termId), where('status', '==', 'approved')),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtProposal)), () => setRows([]))
  }, [termId, enabled])
  return rows
}

export function useCheckins(termId: string, enabled: boolean) {
  const [rows, setRows] = useState<NtCheckin[] | null>(null)
  useEffect(() => {
    if (!termId || !enabled) { setRows(null); return }
    return onSnapshot(query(collection(db, 'ntCheckins'), where('termId', '==', termId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as NtCheckin)), () => setRows([]))
  }, [termId, enabled])
  return rows
}

export async function addCheckin(c: Omit<NtCheckin, 'id' | 'at'>) {
  await addDoc(collection(db, 'ntCheckins'), { ...c, note: c.note.trim(), at: serverTimestamp() })
}

/** Join two students' projects (or undo it), on both proposals at once. */
export async function linkPartners(a: NtProposal, b: NtProposal, joined: boolean) {
  const batch = writeBatch(db)
  const set = (p: NtProposal, other: string) => {
    const cur = p.partnerEmails ?? []
    const next = joined ? [...new Set([...cur, other])] : cur.filter((e) => e !== other)
    batch.update(doc(db, 'ntProposals', p.id), { partnerEmails: next, updatedAt: serverTimestamp() })
  }
  set(a, b.studentEmail); set(b, a.studentEmail)
  await batch.commit()
}

/* ---------------- email ---------------- */

import { limit } from 'firebase/firestore'
import type { MailLogEntry, MailStatus } from '../../core/types'

/** undefined while loading, null if the script has never run. */
export function useMailStatus(enabled: boolean) {
  const [s, setS] = useState<MailStatus | null | undefined>(undefined)
  useEffect(() => {
    if (!enabled) return
    return onSnapshot(doc(db, 'mailStatus', 'script'), (d) => setS(d.exists() ? (d.data() as MailStatus) : null), () => setS(null))
  }, [enabled])
  return s
}

export function useMailLog(enabled: boolean, n = 25) {
  const [rows, setRows] = useState<MailLogEntry[] | null>(null)
  useEffect(() => {
    if (!enabled) return
    return onSnapshot(query(collection(db, 'mailLog'), orderBy('at', 'desc'), limit(n)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as MailLogEntry)), () => setRows([]))
  }, [enabled, n])
  return rows
}
